import { useCallback, useEffect, useMemo } from "react";

import { useBalanceChecker } from "app/components/common";
import { Result, roundDownToDecimals } from "@bridgesplit/utils";
import BN from "bn.js";
import { getZcLedger } from "@bridgesplit/abf-react";

import { AUTO_SLIPPAGE_DECIMALS, useRaydiumContext } from "../RaydiumContext";
import { useRaydiumLtv } from "../util";
import { getRemoveLiquidityQuote } from "../raydium";
import { useClmmRefinanceInfo } from "../../clmm/util";

export function useWithdrawRaydiumBalances() {
    const {
        raydiumPosition: { tokenA, tokenB }
    } = useRaydiumContext();
    const { tokenAAmount, tokenBAmount, maxWithdrawPercent } = useWithdrawAmounts();

    const A = useBalanceChecker({
        mint: tokenA.metadata.mint,
        amount: tokenAAmount,
        customBalance: roundDownToDecimals(tokenA.amount * maxWithdrawPercent, tokenA.metadata.decimals)
    });

    const B = useBalanceChecker({
        mint: tokenB.metadata.mint,
        amount: tokenBAmount,
        customBalance: roundDownToDecimals(tokenB.amount * maxWithdrawPercent, tokenB.metadata.decimals)
    });

    return { A, B };
}

export function useWithdrawAmounts() {
    const {
        withdrawForm,
        loanExpanded,
        raydiumPosition: { totalPrice, tokenA, tokenB }
    } = useRaydiumContext();

    const firstLedger = getZcLedger(loanExpanded);
    const debt = firstLedger?.ledgerDebt;

    const { refinanceInfo } = useClmmRefinanceInfo(loanExpanded);
    const ltv = refinanceInfo?.ltv;

    const tokenAAmount = withdrawForm.percentToWithdraw
        ? tokenA.amount * withdrawForm.percentToWithdraw
        : withdrawForm.percentToWithdraw;
    const tokenBAmount = withdrawForm.percentToWithdraw
        ? tokenB.amount * withdrawForm.percentToWithdraw
        : withdrawForm.percentToWithdraw;

    const currentUsdValue = totalPrice;
    const minCollateralUsd = ltv ? (debt?.total ?? 0) / ltv : 0;

    const maxWithdrawPercent = Math.max(currentUsdValue - minCollateralUsd, 0) / currentUsdValue;
    return { tokenAAmount, tokenBAmount, maxWithdrawPercent };
}

export function useWithdrawRaydiumHooks() {
    useMaintainWithdrawQuote();
}

function useMaintainWithdrawQuote() {
    const {
        raydiumPosition,
        poolData,
        raydiumOffchain,
        withdrawForm,
        positionData,
        setWithdrawForm,
        setWithdrawQuote,
        slippageController: { slippagePercentDecimals }
    } = useRaydiumContext();
    const fetchYieldData = useCallback(async () => {
        try {
            if (!poolData || !raydiumOffchain || !positionData) return;

            const precision = 10_000;
            const bpsToWithdraw = (withdrawForm.percentToWithdraw ?? 0) * precision;

            //Might have to be fresh position, position data
            const deltaLiquidity = raydiumPosition.position.liquidity.mul(new BN(bpsToWithdraw)).div(new BN(precision));

            if (!deltaLiquidity) return;
            setWithdrawForm((prev) => ({
                ...prev,
                status: prev.status === "silent-refetch" ? prev.status : "refetching"
            }));

            const quote = getRemoveLiquidityQuote({
                position: raydiumPosition,
                liquidity: new BN(deltaLiquidity),
                slippage: slippagePercentDecimals ?? AUTO_SLIPPAGE_DECIMALS
            });

            setWithdrawQuote(quote);
        } catch (error) {
            Result.err(error);
            setWithdrawForm((prev) => ({ ...prev, status: "error" }));
        }
    }, [
        poolData,
        raydiumOffchain,
        positionData,
        withdrawForm.percentToWithdraw,
        setWithdrawForm,
        raydiumPosition,
        slippagePercentDecimals,
        setWithdrawQuote
    ]);

    useEffect(() => {
        if (withdrawForm.status && ["refetch-needed", "silent-refetch"].includes(withdrawForm.status)) {
            fetchYieldData();
        }
    }, [fetchYieldData, withdrawForm.status]);

    return { fetchYieldData };
}

export function useUpdateWithdrawAmount() {
    const {
        raydiumPosition: { tokenA, tokenB },
        setWithdrawForm
    } = useRaydiumContext();

    return useCallback(
        (amount: number | undefined, side: "A" | "B") => {
            setWithdrawForm((prev) => {
                const token = side === "A" ? tokenA : tokenB;
                const previousAmount = token.amount * (prev.percentToWithdraw ?? 0);
                if (previousAmount === amount) return prev;
                const percentToWithdraw = amount ? amount / token.amount : amount;
                return { ...prev, percentToWithdraw, side, status: "refetch-needed" };
            });
        },
        [setWithdrawForm, tokenA, tokenB]
    );
}

export function useWithdrawLtv() {
    const rp = useRaydiumUsdValue();
    return useRaydiumLtv(rp?.positionUsdValue);
}

function useRaydiumUsdValue() {
    const { raydiumPosition, poolData, raydiumOffchain } = useRaydiumContext();
    const { tokenAAmount, tokenBAmount } = useWithdrawAmounts();

    return useMemo(() => {
        if (!poolData || !raydiumOffchain) return undefined;

        const remainingTokenAAmount = raydiumPosition.tokenA.amount - (tokenAAmount ?? 0);
        const remainingTokenBAmount = raydiumPosition.tokenB.amount - (tokenBAmount ?? 0);

        const tokenAUsd = remainingTokenAAmount * raydiumPosition.tokenA.usdPrice;
        const tokenBUsd = remainingTokenBAmount * raydiumPosition.tokenB.usdPrice;

        const positionUsdValue = tokenAUsd + tokenBUsd;
        return { positionUsdValue, tokenAUsd, tokenBUsd };
    }, [
        poolData,
        raydiumOffchain,
        raydiumPosition.tokenA.amount,
        raydiumPosition.tokenA.usdPrice,
        raydiumPosition.tokenB.amount,
        raydiumPosition.tokenB.usdPrice,
        tokenAAmount,
        tokenBAmount
    ]);
}
